/* eslint-disable import/prefer-default-export */
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Popper,
  Select,
  TextField,
} from "@mui/material";
import { PopperProps } from "@mui/material/Popper/BasePopper.types";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toHiragana } from "../utils/common";

type IncrementalProps = {
  label: string;
  options: string[];
  value: string | string[];
  onChange: Function;
  optionLength?: number;
  multiple?: boolean;
  fullWidth?: boolean;
  isBlankNoOptions?: boolean; // 最初に検索画面が空の場合にオプションを隠す
};

/**
 * ポッパーコンポーネント
 * @param props Popperのプロップス
 * @returns JSX.Element
 */
function PopperComponent(props: PopperProps) {
  return <Popper {...props} placement="bottom-start" />;
}

/**
 * インクリメンタルサーチ
 * @param param0 プロップス
 * @returns JSX.Element
 */
export function IncrementalSearchElement({
  label,
  options,
  value,
  onChange,
  optionLength,
  multiple,
  fullWidth,
  isBlankNoOptions = true,
}: IncrementalProps) {
  const [inputValueBackup, setInputValueBackup] = useState("");
  const [selectValueBackup, setSelectValueBackup] = useState("");
  const [open, setOpen] = useState(false);
  const filterOptions =
    (): ((opts: string[], { inputValue }: { inputValue: string }) => string[]) =>
    (opts: string[], { inputValue }: { inputValue: string }) =>
      opts
        .filter((o) => toHiragana(o).includes(toHiragana(inputValue)))
        .slice(0, optionLength ?? 5);
  useEffect(() => {
    setOpen(
      !(inputValueBackup === "" && selectValueBackup === "") &&
        !(inputValueBackup === selectValueBackup),
    );
  }, [inputValueBackup, selectValueBackup]);
  useEffect(() => {
    if (!multiple) {
      setInputValueBackup((value as string) ?? "");
      setSelectValueBackup((value as string) ?? "");
    }
  }, [value]);

  return (
    <Box m={1} width={fullWidth ? "calc(100% - 16px)" : "calc(50% - 16px)"}>
      {multiple ? (
        <Autocomplete
          // freeSolo
          multiple
          fullWidth
          // eslint-disable-next-line react/no-unstable-nested-components
          PopperComponent={PopperComponent}
          options={options}
          // filterOptions={(opts, {inputValue}) =>
          //   opts.filter(o => toHiragana(o).includes(toHiragana(inputValue))).slice(0, optionLength ?? 5)
          // }
          value={value as string[]}
          onChange={(e, val) => onChange(val)}
          // onInputChange={(e, value) => handleStorehouseChange(value)}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
          limitTags={2}
        />
      ) : (
        <Autocomplete
          // freeSolo
          fullWidth
          // eslint-disable-next-line react/no-unstable-nested-components
          open={isBlankNoOptions ? open : undefined}
          onInputChange={(e, val) => {
            if (isBlankNoOptions) setInputValueBackup(val);
          }}
          popupIcon={null}
          PopperComponent={PopperComponent}
          options={options.map((s) => s)}
          filterOptions={filterOptions()}
          value={(value as string) || null}
          onChange={(e, val) => {
            setSelectValueBackup(val ?? "");
            onChange(val);
          }}
          onBlur={() => setOpen(false)}
          sx={{ height: "40px", maxWidth: 223 }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
        />
      )}
    </Box>
  );
}

type SelectFormProps = {
  id: string;
  label: string;
  optionList: string[];
  value: string;
  setValue: Dispatch<SetStateAction<string>> | Function;
  required?: boolean;
  disabled?: boolean;
  isContainBlank?: boolean;
  width?: string | number;
};

/**
 * 選択フォーム
 * @param root0 選択フォームのプロップス
 * @returns JSX.Element
 */
export function SelectForm({
  id,
  label,
  optionList,
  value,
  setValue,
  required,
  disabled,
  isContainBlank,
  width,
}: SelectFormProps) {
  return (
    <Box width="50%" m={0}>
      <Box p={1}>
        <FormControl sx={{ minWidth: 160, maxWidth: width ?? "inherit" }} required={required}>
          <InputLabel
            id={`${id}-label`}
            sx={{
              top: "-6px",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -4px) scale(0.75)",
              },
              "& .MuiFormLabel-asterisk": {
                color: "var(--red-800)",
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`${id}-label`}
            label={label}
            id={id}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ height: "40px" }}
            disabled={disabled}
          >
            {isContainBlank && (
              // eslint-disable-next-line no-irregular-whitespace, react/self-closing-comp
              <MenuItem key="" value="">
                &emsp;
              </MenuItem>
            )}
            {optionList.map((s) => (
              <MenuItem key={s} value={s}>
                {s === "" ? '\u2003' : s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
